import { useMemo } from 'react'

import { Table, Tag } from 'antd'
import { useSelector } from 'react-redux'

import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import { formatDate, formatStampDateTime } from '../../../utils/entities/aphmAnomaly'
import { getStateTag } from '../../../utils/entities/internship'

const TAG_STYLE = { borderRadius: '8px', fontWeight: 500 }

const DIFFERING_STYLE = { background: '#fffbe6' }

const EMPTY = '—'

const LABEL_COLUMN_WIDTH = 190

const INTERNSHIP_COLUMN_WIDTH = 240

const fullName = link => [link.studentFirstname, link.studentLastname].filter(Boolean).join(' ')

const period = link => [formatDate(link.startDate), formatDate(link.endDate)].filter(Boolean).join(' → ')

const stateCell = (link, t) => {
  const tag = getStateTag(link.state)

  return tag ? <Tag color={tag.color} style={TAG_STYLE}>{t(tag.label)}</Tag> : link.state
}

const FIELDS = [
  { key: 'internshipId', label: 'aphm_anomaly.internship_id', value: link => link.internshipId },
  { key: 'student', label: 'common.student', value: fullName },
  { key: 'studentEmail', label: 'common.email', value: link => link.studentEmail },
  { key: 'period', label: 'common.period', value: period },
  { key: 'state', label: 'common.state', value: link => link.state, render: stateCell },
  { key: 'sectorName', label: 'common.sector', value: link => link.sectorName },
  { key: 'sectionName', label: 'common.section', value: link => link.sectionName },
  { key: 'shiftCount', label: 'aphm_anomaly.snapshot.shiftCount', value: link => link.shiftCount },
  { key: 'desiderataCount', label: 'aphm_anomaly.snapshot.desiderataCount', value: link => link.desiderataCount },
  { key: 'shiftMinutes', label: 'aphm_anomaly.snapshot.shiftMinutes', value: link => link.shiftMinutes },
  { key: 'ownership', label: 'aphm_anomaly.snapshot.ownership', value: link => link.ownership },
  { key: 'createdAt', label: 'aphm_anomaly.snapshot.createdAt', value: link => formatStampDateTime(link.createdAt) },
  { key: 'updatedAt', label: 'aphm_anomaly.snapshot.updatedAt', value: link => formatStampDateTime(link.updatedAt) }
]

const metadataKeys = links => {
  const keys = []

  links.forEach(link => Object.keys(link?.metadata ?? {}).forEach(key => {
    if (!keys.includes(key)) { keys.push(key) }
  }))

  return keys
}

const metadataFields = links => metadataKeys(links).map(key => ({
  key: 'metadata.' + key,
  label: 'aphm_anomaly.metadata.' + key,
  value: link => link?.metadata?.[key] ?? null
}))

const isDiffering = (field, links) => new Set(links.map(link => JSON.stringify(field.value(link) ?? null))).size > 1

const buildRows = links => [...FIELDS, ...metadataFields(links)].map(field => ({
  key: field.key,
  field,
  differing: isDiffering(field, links)
}))

const AphmAnomalyLinkedInternships = ({ links }) => {
  const t = useSelector(selectTranslate)

  const rows = useMemo(() => buildRows(links), [links])

  const columns = useMemo(() => [
    {
      title: '',
      key: 'label',
      width: LABEL_COLUMN_WIDTH,
      render: (text, row) => <span style={{ fontWeight: 500 }}>{t(row.field.label)}</span>
    },
    ...links.map((link, index) => ({
      title: t('common.internship') + ' #' + (link.internshipId ?? index + 1),
      key: link.internshipId ?? index,
      width: INTERNSHIP_COLUMN_WIDTH,
      render: (text, row) => {
        const value = row.field.value(link)

        if (row.field.render) {
          return row.field.render(link, t)
        }

        if (value === null || value === undefined || value === '') {
          return <span>{EMPTY}</span>
        }

        if (typeof value === 'boolean') {
          return <span>{value ? '✓' : '✗'}</span>
        }

        return <span>{String(value)}</span>
      }
    }))
  ], [links, t])

  return (
    <Table
      columns={columns}
      dataSource={rows}
      size='small'
      pagination={false}
      scroll={{ x: LABEL_COLUMN_WIDTH + links.length * INTERNSHIP_COLUMN_WIDTH }}
      onRow={row => (row.differing ? { style: DIFFERING_STYLE } : {})}
    />
  )
}

export default AphmAnomalyLinkedInternships
